import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  ArrowRight,
  Loader2,
  Clock,
  FileText,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { onboardingService } from "@/services";

interface LeadContext {
  leadId: string;
  email: string;
  name: string;
  packageId: string | null;
  timezone: string;
  meetingLength: number;
  ndaRequired: boolean;
  emailVerified?: boolean;
  ndaSigned?: boolean;
  summary: string;
}

function getLeadContext(): LeadContext | null {
  try {
    const raw = localStorage.getItem("echopeak-lead-context");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function NDAGatePage() {
  const navigate = useNavigate();
  const [leadContext, setLeadContext] = useState<LeadContext | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctx = getLeadContext();
    if (!ctx || !ctx.emailVerified) {
      navigate("/verify/email");
      return;
    }
    if (!ctx.ndaRequired) {
      navigate("/engage");
      return;
    }
    setLeadContext(ctx);
  }, [navigate]);

  const handleReviewAndSign = async () => {
    if (!leadContext) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await onboardingService.createNdaContract({
        leadId: leadContext.leadId,
        name: leadContext.name,
        email: leadContext.email,
        summary: leadContext.summary,
      });

      const updated = {
        ...leadContext,
        ndaEnvelopeId: response.envelopeId,
      };
      localStorage.setItem("echopeak-lead-context", JSON.stringify(updated));

      window.open(response.signingUrl, "_blank");
      navigate("/nda/complete");
    } catch (err) {
      console.error("Failed to create NDA:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to prepare NDA. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!leadContext) return null;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg w-full relative z-10"
      >
        <Card className="glass-card border-gradient">
          <CardContent className="pt-8 pb-6 px-6 md:px-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-8 w-8 text-primary" />
              </div>
              <h1 className="font-display text-2xl font-bold mb-2">
                Non-Disclosure Agreement
              </h1>
              <p className="text-muted-foreground text-sm">
                Based on your project details, an NDA is required before we can
                schedule the discovery call. This protects your sensitive
                information.
              </p>
            </div>

            <div className="space-y-6">
              <div className="rounded-lg border border-border/50 divide-y divide-border/50">
                <div className="p-4 flex items-start gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">What the NDA covers</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Mutual confidentiality obligations for proprietary
                      information, datasets, credentials, and business
                      strategies shared during the engagement.
                    </p>
                  </div>
                </div>
                <div className="p-4 flex items-start gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Quick to sign</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Takes less than 2 minutes. Signed securely via DocuSign.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 text-sm">
                <p className="font-medium mb-1">Signing as:</p>
                <p className="text-muted-foreground">
                  {leadContext.name} — {leadContext.email}
                </p>
              </div>

              {error && (
                <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-sm flex items-start gap-3">
                  <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                  <p className="text-destructive">{error}</p>
                </div>
              )}

              <Button
                onClick={handleReviewAndSign}
                disabled={isLoading}
                className="w-full"
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Preparing NDA...
                  </>
                ) : (
                  <>
                    Review & Sign NDA
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
